import config from '../config'
import InternalToolsService from '../../../services/InternalToolsService'
import { requests } from '.'

const subdomainTwilio = '/conversations/providers/twilio'

export const LocationNumbersService = {
  getPhoneNumbers: async (locationId: string, payload: any) => {
    const { limit, page, query = '' } = payload
    try {
      const { data } = await requests(config.APP_ENGINE_API_URL).get(
        `/location/${locationId}/numbers?limit=${limit}&page=${page}&query=${query}`
      )
      return Promise.resolve(data)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getInboundPhoneNumbers: async (
    locationId: string,
    twilioAccountId: string
  ) => {
    try {
      const { data } = await requests(config.APP_ENGINE_API_URL).get(
        `twilio/get_inbound_numbers/${twilioAccountId}?location_id=${locationId}&skipNumberPool=true`
      )
      return Promise.resolve(data)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getPhoneNumbersSID: async (locationId: string) => {
    try {
      const { data } = await requests(config.MARKETPLACE_REST_API_URL).get(
        `/phone-system/twilio-accounts/numbers-sid-map?locationId=${locationId}`
      )
      return Promise.resolve(data)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getNumberPools: async (numberPools: string[]) => {
    try {
      const { data } = await requests(config.MARKETPLACE_REST_API_URL).post(
        `/phone-system/number-pools`,
        {
          numberPools: numberPools,
        }
      )
      return Promise.resolve(data)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getDetailedTwilioAccount: async (locationId: string) => {
    try {
      const { data } = await requests(config.MARKETPLACE_REST_API_URL).get(
        `/phone-system/twilio-accounts?entityId=${locationId}&entityType=LOCATION`
      )
      return Promise.resolve(data)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getCampaigns: async (locationId: string, companyId: string) => {
    try {
      const { data } = await requests(config.ISV).get(
        `/compliance/${companyId}/campaigns/${locationId}`
      )
      return Promise.resolve(data)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getCallerIDs: async (locationId: string) => {
    try {
      const { data } = await requests(config.ISV).get(
        `/caller-id/location/${locationId}`
      )
      return Promise.resolve(data)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getCountryNumbers: async (payload: any) => {
    const {
      locationId,
      countryCode,
      firstPart,
      lastPart,
      anywhere,
      smsEnabled,
      voiceEnabled,
      mmsEnabled,
      numberTypes,
    } = payload

    try {
      const { data } = await requests(config.APP_ENGINE_API_URL).get(
        `/twilio/get_numbers/${locationId}`,
        {
          params: {
            countryCode,
            firstPart,
            lastPart,
            anywhere,
            smsEnabled,
            voiceEnabled,
            mmsEnabled,
            numberTypes,
          },
        }
      )
      return Promise.resolve(data)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getLocationTwilio: async locationId => {
    try {
      const { data } = await requests(config.MARKETPLACE_REST_API_URL).get(
        `${subdomainTwilio}/fetch?locationId=${locationId}`
      )
      return Promise.resolve(data)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getLocationAddresses: async (locationId, countryCode) => {
    try {
      const { data } = await requests(config.MARKETPLACE_REST_API_URL).get(
        `twilio/get_addresses/${locationId}/fetch?country_code=${countryCode}`
      )
      return Promise.resolve(data)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getLocationBundles: async (locationId, countryCode) => {
    try {
      const { data } = await requests(config.MARKETPLACE_REST_API_URL).get(
        `twilio/get_addresses/${locationId}/fetch?country_code=${countryCode}`
      )
      return Promise.resolve(data)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  setPurchaseNumber: async (locationId: string, payload: any) => {
    const {
      addressSid,
      bundleSid,
      countryCode,
      numberType,
      phoneNumber,
      stripeAccountId,
      locality,
      region,
    } = payload

    try {
      const res = await requests(config.APP_ENGINE_API_URL).get(
        `/twilio/purchase_number/${locationId}`,
        {
          params: {
            addressSid,
            bundleSid,
            countryCode,
            numberType,
            phone_number: phoneNumber,
            stripeAccountId,
            locality,
            region,
          },
        }
      )
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  setPurchaseNumberWithStripe: async (locationId: string, payload: any) => {
    const {
      countryCode,
      phoneNumber,
      numberType,
      addressSid,
      bundleSid,
      stripeAccountId,
      stripeCustomerId,
      stripe,
      locality,
      region,
    } = payload

    const entity = {
      entityId: locationId,
      entityType: 'LOCATION',
    }

    let lastPaymentError = null

    try {
      const stripePayload ={
        ...entity,
        amount: 1,
        stripeCustomerId: stripeCustomerId,
        stripeAccountId: stripeAccountId,
        description: 'PhoneNumberPurchase - 3DS verification',
        enable3DS: true,
      }

      const  customerPaymentMethod = await InternalToolsService.getStripeCustomer(entity as any)

      const { data: createdPaymentIntent } = await requests(config.SAAS).post(
        `/saas-config/${locationId}/create-stripe-payment-intent`,
        stripePayload
      )

      const confirmation = await stripe.confirmCardPayment(
        createdPaymentIntent?.client_secret,
        {
          payment_method: customerPaymentMethod?.invoiceSettings?.defaultPaymentMethod?.id
        }
      )

      lastPaymentError = confirmation?.paymentIntent?.last_payment_error

      if (!confirmation?.paymentIntent) {
        const error = {
          response: {
            data: {
              message: confirmation?.error?.message,
            },
          },
        }
        return Promise.reject(error)
      }

      const paymentIntentId = confirmation?.paymentIntent?.id

      const res = await requests(config.APP_ENGINE_API_URL).get(
        `/twilio/purchase_number/${locationId}`,
        {
          params: {
            phone_number: phoneNumber,
            addressSid,
            bundleSid,
            numberType,
            paymentIntentId,
            stripeAccountId,
            countryCode,
            locality,
            region,
          },
        }
      )
      return Promise.resolve(res)
    } catch (error) {
      if (lastPaymentError) error.response.data = { message: lastPaymentError }
      return Promise.reject(error)
    }
  },

  deleteNumber: async (locationId: string, payload: any) => {
    try {
      const { data } = await requests(config.APP_ENGINE_API_URL).get(
        `/twilio/delete_number/${locationId}`,
        payload
      )
      return Promise.resolve(data)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  deleteCallerID: async (locationId: string, sid: string) => {
    try {
      const { data } = await requests(config.ISV).delete(
        `/caller-id/location/${locationId}?sid=${sid}`
      )
      return Promise.resolve(data)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  deletePool: async (locationId: string, payload: any) => {
    try {
      const { data } = await requests(config.APP_ENGINE_API_URL).post(
        `/twilio/delete_pool/${locationId}`,
        payload
      )
      return Promise.resolve(data)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getRegulatoryBundles: async (payload: any) => {
    try {
      const { locationId, countryCode } = payload
      const res = await requests(config.APP_ENGINE_API_URL).get(
        `/twilio/get_bundles/${locationId}?country_code=${countryCode}`
      )
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getRegulatoryAddresses: async (payload: any) => {
    try {
      const { locationId, countryCode } = payload
      const res = await requests(config.APP_ENGINE_API_URL).get(
        `/twilio/get_addresses/${locationId}?country_code=${countryCode}`
      )
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getVerificationCall: async (payload: any) => {
    const { locationId, phoneNumber } = payload

    try {
      const res = await requests(config.ISV).post(
        `/caller-id/location/${locationId}/verification-code`,
        {
          phoneNumber,
        }
      )
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getVerificationStatus: async (payload: any) => {
    const { locationId, phoneNumber } = payload

    try {
      const res = await requests(config.SAAS).get(
        `/twilio-accounts/${locationId}/${phoneNumber}`
      )
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getA2PBrandStatus: async (payload: any) => {
    const { companyId, locationId, brandType } = payload

    try {
      const res = await requests(config.ISV).get(
        `/compliance/${companyId}/${
          brandType + (brandType === 'standard' ? '-registration' : '')
        }/${locationId}/campaign?brandType=${brandType}`
      )
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  updateVerifiedCallerId: async (payload: any) => {
    const { sid, friendlyName, locationId } = payload

    try {
      const res = await requests(config.ISV).put(
        `/caller-id/location/${locationId}`,
        {
          friendlyName,
          sid,
        }
      )
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  updatePhoneNumber: async (locationId: string, payload: any) => {
    try {
      const res = await requests(config.MARKETPLACE_REST_API_URL).post(
        `/phone-system/twilio-accounts?locationId=${locationId}`,
        payload
      )
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  updateNumberPool: async (
    locationId: string,
    numberPoolId: string,
    payload: any
  ) => {
    try {
      const res = await requests(config.MARKETPLACE_REST_API_URL).post(
        `/phone-system/number-pools/update?numberPoolId=${numberPoolId}&locationId=${locationId}`,
        payload
      )
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  updateTwilioNumber: async (locationId: string, payload: any) => {
    const { phoneNumber, sid, isDefaultOutboundNumber } = payload

    try {
      const res = await requests(config.baseURL).get(
        `/twilio/update_number/${locationId}`,
        {
          params: {
            phone_number: phoneNumber,
            number_id: sid,
            defaultNumber: isDefaultOutboundNumber,
          },
        }
      )
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  checkAvailableNumbers: async (locationId: string, payload: any) => {
    const { countryISO, poolSize, action, areaCode } = payload

    try {
      let url = `/twilio/check_available_numbers/${locationId}/${countryISO}/${poolSize}/${
        action === 'custom' ? 'local' : action
      }`
      url = areaCode ? `${url}/${areaCode}` : url
      const res = await requests(config.baseURL).get(url)

      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  purchaseNumberPool: async (locationId: string, payload: any) => {
    try {
      const res = await requests(config.baseURL).post(
        `/twilio/purchase_number_pool/${locationId}`,
        payload
      )
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getTollFreeVerificationStatus: async (
    companyId: string,
    locationId: string,
    tollFreePhoneNumberSID: string
  ) => {
    try {
      const res = await requests(config.ISV).get(
        `tollFree/${companyId}/fetch/${locationId}?tollfreePhoneNumberSid=${tollFreePhoneNumberSID}`
      )
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  createTollFreeVerificationRequest: async (
    companyId: string,
    locationId: string,
    payload: any
  ) => {
    try {
      const res = await requests(config.ISV).post(
        `tollFree/${companyId}/create/${locationId}`,
        payload
      )
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  reSubmitTollFreeVerificationRequest: async (
    companyId: string,
    locationId: string,
    tollFreeVerificationSID: string,
    payload: any
  ) => {
    try {
      const res = await requests(config.ISV).post(
        `tollFree/${companyId}/resubmit/${locationId}?tollfreeVerificationSid=${tollFreeVerificationSID}`,
        payload
      )
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getA2PStatus: async (companyId: string, locationId: string) => {
    try {
      const res = await requests(config.ISV).get(
        `/compliance/${companyId}/linked-numbers/${locationId}`
      )
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getTollFreeStatus: async (companyId: string, locationId: string) => {
    try {
      const res = await requests(config.ISV).get(
        `/tollFree/${companyId}/list/${locationId}`
      )
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  linkPhoneNumber: async (
    companyId: string,
    locationId: string,
    payload: any
  ) => {
    try {
      const res = await requests(config.ISV).post(
        `/compliance/${companyId}/link-number-to-selected-messaging-service/${locationId}`,
        payload
      )
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },
}
